import { DisposableGroup } from '@blocksuite/global/disposable';
import { deserializeXYWH } from '@blocksuite/global/gfx';
import { type Constructor } from '@blocksuite/global/utils';
import {
  assertCanvasWorldXYWH,
  BlockModel,
  nanoid,
  type PageStructureChange,
  placeCanvasWorldXYWH,
} from '@blocksuite/store';
import { signal } from '@preact/signals-core';
import {
  type PageElementUnit,
  PageStructureWriteError,
} from '@streamlain/database-identity';
import { Subject } from 'rxjs';
import * as Y from 'yjs';

import {
  type GfxGroupCompatibleInterface,
  isGfxGroupCompatibleModel,
} from '../base.js';
import type { GfxGroupModel, GfxModel } from '../model.js';
import { createDecoratorState } from './decorators/common.js';
import { initializeObservers, initializeWatchers } from './decorators/index.js';
import {
  GfxGroupLikeElementModel,
  type GfxPrimitiveElementModel,
  syncElementFromY,
} from './element-model.js';
import type { GfxLocalElementModel } from './local-element-model.js';

/**
 * Used for text field
 */
export const SURFACE_TEXT_UNIQ_IDENTIFIER = 'affine:surface:text';
/**
 * Used for field that use Y.Map. E.g. group children field
 */
export const SURFACE_YMAP_UNIQ_IDENTIFIER = 'affine:surface:ymap';

/** Group members given as anything but a plain object of member IDs. */
export class GfxStructureMembersError extends TypeError {
  override readonly name = 'GfxStructureMembersError';
}

/** The member IDs of a group's members prop: the keys of a plain object. */
function structureMemberIds(value: unknown): string[] {
  const prototype =
    value !== null && typeof value === 'object'
      ? Object.getPrototypeOf(value)
      : undefined;
  if (
    (prototype !== Object.prototype && prototype !== null) ||
    Reflect.has(value as object, SURFACE_YMAP_UNIQ_IDENTIFIER)
  )
    throw new GfxStructureMembersError(
      'Structure members must be a plain object of member IDs'
    );
  return Object.keys(value as object);
}

/**
 * The surface keeps no fields of its own: its canvas elements live in the
 * elements map of the page part, each placed on it by a structure entry.
 */
export type SurfaceBlockProps = Record<string, never>;

export interface ElementUpdatedData {
  id: string;
  props: Record<string, unknown>;
  oldValues: Record<string, unknown>;
  local: boolean;
}

export type MiddlewareCtx = {
  type: 'beforeAdd';
  payload: {
    type: string;
    props: Record<string, unknown>;
  };
};

export type SurfaceMiddleware = (ctx: MiddlewareCtx) => void;

export class SurfaceBlockModel extends BlockModel<SurfaceBlockProps> {
  protected _decoratorState = createDecoratorState();

  protected _elementCtorMap: Record<
    string,
    Constructor<
      GfxPrimitiveElementModel,
      ConstructorParameters<typeof GfxPrimitiveElementModel>
    >
  > = Object.create(null);

  protected _elementModels = new Map<
    string,
    {
      mount: () => void;
      unmount: () => void;
      model: GfxPrimitiveElementModel;
    }
  >();

  protected _elementTypeMap = new Map<string, GfxPrimitiveElementModel[]>();

  /** Layer index of every element as last announced, for its old value. */
  private readonly _elementOrders = new Map<string, string>();

  /** Elements added here whose placement is still to be observed. */
  private readonly _pendingElements = new Set<string>();

  protected _groupLikeModels = new Map<string, GfxGroupModel>();

  protected _middlewares: SurfaceMiddleware[] = [];

  protected _surfaceBlockModel = true;

  protected localElements = new Set<GfxLocalElementModel>();

  elementAdded = new Subject<{ id: string; local: boolean }>();

  elementRemoved = new Subject<{
    id: string;
    type: string;
    model: GfxPrimitiveElementModel;
    local: boolean;
  }>();

  elementUpdated = new Subject<ElementUpdatedData>();

  localElementAdded = new Subject<GfxLocalElementModel>();

  localElementDeleted = new Subject<GfxLocalElementModel>();

  localElementUpdated = new Subject<{
    model: GfxLocalElementModel;
    props: Record<string, unknown>;
    oldValues: Record<string, unknown>;
  }>();

  private readonly _isEmpty$ = signal(false);

  /**
   * Find a child element by ID for _parentGroupId management.
   * Looks up both surface elements and block models.
   */
  protected _findChildForParentGroup(
    childId: string
  ): { _parentGroupId: string | null } | null {
    const surfaceChild = this.getElementById(childId);
    if (surfaceChild) return surfaceChild;
    const block = this.store.getBlock(childId);
    if (block?.model && '_parentGroupId' in block.model) {
      return block.model as { _parentGroupId: string | null };
    }
    return null;
  }

  private _forEachParentGroupCandidate(
    callback: (id: string, child: { _parentGroupId: string | null }) => void
  ) {
    this._elementModels.forEach(({ model }) => callback(model.id, model));
    Object.values(this.store.blocks.peek()).forEach(block => {
      if ('_parentGroupId' in block.model) {
        callback(block.id, block.model as { _parentGroupId: string | null });
      }
    });
  }

  private _findParentGroupForChild(childId: string): GfxGroupModel | null {
    for (const group of this._groupLikeModels.values()) {
      if (group.id !== childId && group.childIds.includes(childId)) {
        return group;
      }
    }
    return null;
  }

  private _syncParentGroupRefForChild(
    childId: string,
    child = this._findChildForParentGroup(childId)
  ) {
    if (!child) return;
    child._parentGroupId = this._findParentGroupForChild(childId)?.id ?? null;
  }

  private _syncParentGroupRefsForGroup(group: GfxGroupModel) {
    const currentChildIds = new Set(group.childIds);

    this._forEachParentGroupCandidate((childId, child) => {
      if (child._parentGroupId === group.id && !currentChildIds.has(childId)) {
        this._syncParentGroupRefForChild(childId, child);
      }
    });

    for (const childId of group.childIds) {
      const child = this._findChildForParentGroup(childId);
      if (child) child._parentGroupId = group.id;
    }
  }

  private _registerGroupLikeModel(id: string, model: GfxGroupModel) {
    this._groupLikeModels.set(id, model);
    this._syncParentGroupRefsForGroup(model);
    if ('_parentGroupId' in model) {
      this._syncParentGroupRefForChild(
        id,
        model as { _parentGroupId: string | null }
      );
    }
  }

  private _unregisterGroupLikeModel(id: string) {
    if (!this._groupLikeModels.has(id)) return;
    this._groupLikeModels.delete(id);
    this._forEachParentGroupCandidate((childId, child) => {
      if (child._parentGroupId === id) {
        this._syncParentGroupRefForChild(childId, child);
      }
    });
  }

  get elementModels() {
    const models: GfxPrimitiveElementModel[] = [];
    this._elementModels.forEach(model => models.push(model.model));
    return models;
  }

  /** Fields of an element from its loaded part. */
  elementFields(id: string): Y.Map<unknown> | undefined {
    return this.store.pageStructure.elementFields(id);
  }

  /** Ids of the elements drawn on this surface, in layer order. */
  get elementIds(): readonly string[] {
    return this.store.pageStructure.tree().elements.get(this.id) ?? [];
  }

  get localElementModels() {
    return this.localElements;
  }

  get registeredElementTypes() {
    return Object.keys(this._elementCtorMap);
  }

  override isEmpty(): boolean {
    return this._isEmpty$.value;
  }

  constructor() {
    super();
    const subscription = this.created.subscribe(() => {
      this._init();
      subscription.unsubscribe();
    });
  }

  private _createElementFromProps(
    props: Record<string, unknown>,
    options: {
      onChange: (payload: {
        id: string;
        props: Record<string, unknown>;
        oldValues: Record<string, unknown>;
        local: boolean;
      }) => void;
    }
  ) {
    const { type, id, ...rest } = props;

    if (!id) {
      throw new Error('Cannot find id in props');
    }

    const yMap = new Y.Map();
    const elementModel = this._createElementFromYMap(
      type as string,
      id as string,
      yMap,
      {
        ...options,
        newCreate: true,
      }
    );

    props = this._propsToY(type as string, props);

    yMap.set('type', type);
    yMap.set('id', id);

    Object.keys(rest).forEach(key => {
      if (props[key] !== undefined) {
        // @ts-expect-error ignore
        elementModel.model[key] = props[key];
      }
    });

    return elementModel;
  }

  private _createElementFromYMap(
    type: string,
    id: string,
    yMap: Y.Map<unknown>,
    options: {
      onChange: (payload: {
        id: string;
        props: Record<string, unknown>;
        oldValues: Record<string, unknown>;
        local: boolean;
      }) => void;
      skipFieldInit?: boolean;
      newCreate?: boolean;
    }
  ) {
    const stashed = new Map<string | symbol, unknown>();
    const Ctor = this._elementCtorMap[type];

    if (!Ctor) {
      throw new Error(`Invalid element type: ${yMap.get('type')}`);
    }
    const state = this._decoratorState;

    state.creating = true;
    state.skipField = options.skipFieldInit ?? false;

    let mounted = false;
    // @ts-expect-error ignore
    Ctor['_decoratorState'] = state;

    const elementModel = new Ctor({
      id,
      yMap,
      model: this,
      stashedStore: stashed,
      onChange: payload => mounted && options.onChange({ id, ...payload }),
    }) as GfxPrimitiveElementModel;

    // @ts-expect-error ignore
    delete Ctor['_decoratorState'];
    state.creating = false;
    state.skipField = false;

    const unmount = () => {
      mounted = false;
      elementModel.onDestroyed();
    };

    const mount = () => {
      initializeObservers(Ctor.prototype, elementModel);
      initializeWatchers(Ctor.prototype, elementModel);
      elementModel['_disposable'].add(
        syncElementFromY(elementModel, payload => {
          mounted &&
            options.onChange({
              id,
              ...payload,
            });
        })
      );
      mounted = true;
      elementModel.onCreated();
    };

    return {
      model: elementModel,
      mount,
      unmount,
    };
  }

  private _initElementModels() {
    const structure = this.store.pageStructure;
    const addToType = (type: string, model: GfxPrimitiveElementModel) => {
      const sameTypeElements = this._elementTypeMap.get(type) || [];

      if (sameTypeElements.indexOf(model) === -1) {
        sameTypeElements.push(model);
      }

      this._elementTypeMap.set(type, sameTypeElements);

      if (isGfxGroupCompatibleModel(model)) {
        this._registerGroupLikeModel(model.id, model);
      }
      this._syncParentGroupRefForChild(model.id, model);
    };
    const removeFromType = (type: string, model: GfxPrimitiveElementModel) => {
      const sameTypeElements = this._elementTypeMap.get(type) || [];
      const index = sameTypeElements.indexOf(model);

      if (index !== -1) {
        sameTypeElements.splice(index, 1);
      }

      if (this._groupLikeModels.has(model.id)) {
        this._unregisterGroupLikeModel(model.id);
      }
      model._parentGroupId = null;
    };
    const createFromFields = (id: string, fields: Y.Map<unknown>) => {
      const model = this._createElementFromYMap(
        fields.get('type') as string,
        id,
        fields,
        {
          onChange: payload => {
            this.elementUpdated.next(payload);
            Object.keys(payload.props).forEach(key => {
              model.model.propsUpdated.next({ key });
            });
          },
          skipFieldInit: true,
        }
      );
      return model;
    };
    const syncMembers = (local: boolean) => (id: string) => {
      const model = this._elementModels.get(id)?.model;
      if (model instanceof GfxGroupLikeElementModel) model.syncMembers(local);
    };

    // An element is drawn here when the tree places it on this surface and
    // its fields are there: both arrive in one transaction, their observers
    // in either order.
    const reconcile = (ids: Iterable<string>, local: boolean) => {
      const mine = new Set(this.elementIds);
      const addedElements: {
        mount: () => void;
        model: GfxPrimitiveElementModel;
      }[] = [];
      const deletedElements: {
        unmount: () => void;
        model: GfxPrimitiveElementModel;
      }[] = [];
      const remove = (id: string) => {
        const { model, unmount } = this._elementModels.get(id)!;
        removeFromType(model.type, model);
        this._elementModels.delete(id);
        this._elementOrders.delete(id);
        deletedElements.push({ model, unmount });
      };

      for (const id of ids) {
        const fields = structure.elementFields(id);
        const wanted = mine.has(id) && fields instanceof Y.Map;
        const existing = this._elementModels.get(id);
        const pending = this._pendingElements.has(id);
        // Undo of a delete brings the element back with new fields.
        if (
          existing &&
          !pending &&
          (!wanted || existing.model.yMap !== fields)
        ) {
          remove(id);
        }
        if (!wanted) continue;
        const model = pending
          ? this._elementModels.get(id)!
          : this._elementModels.has(id)
            ? null
            : createFromFields(id, fields);
        if (!model) continue;
        this._pendingElements.delete(id);
        this._elementModels.set(id, model);
        this._elementOrders.set(id, model.model.index);
        addToType(model.model.type, model.model);
        addedElements.push(model);
      }

      addedElements.forEach(({ mount, model }) => {
        mount();
        syncMembers(local)(model.id);
        this.elementAdded.next({ id: model.id, local });
      });
      deletedElements.forEach(({ unmount, model }) => {
        unmount();
        this.elementRemoved.next({
          id: model.id,
          type: model.type,
          model,
          local,
        });
      });
    };

    const onStructureChange = (change: PageStructureChange) => {
      reconcile(change.ids, change.local);
      change.reordered.forEach(id => {
        const element = this._elementModels.get(id)?.model;
        const previous = this._elementOrders.get(id);
        if (!element || previous === undefined) return;
        const index = element.index;
        if (index === previous) return;
        this._elementOrders.set(id, index);
        this.elementUpdated.next({
          id,
          props: { index },
          oldValues: { index: previous },
          local: change.local,
        });
        element.propsUpdated.next({ key: 'index' });
      });
      change.groups.forEach(syncMembers(change.local));
    };
    const onElementsMapChange = (
      event: Y.YMapEvent<Y.Map<unknown>>,
      transaction: Y.Transaction
    ) => reconcile(event.keysChanged, transaction.local);

    this.elementIds.forEach(id => {
      const fields = structure.elementFields(id);
      if (!(fields instanceof Y.Map)) return;
      const model = createFromFields(id, fields);
      this._elementModels.set(id, model);
      this._elementOrders.set(id, model.model.index);
    });

    this._elementModels.forEach(({ mount, model }) => {
      addToType(model.type, model);
      mount();
    });
    this._elementModels.forEach(({ model }) => syncMembers(false)(model.id));

    Object.values(this.store.blocks.peek()).forEach(block => {
      if (isGfxGroupCompatibleModel(block.model)) {
        this._registerGroupLikeModel(block.id, block.model);
      }
    });

    this._groupLikeModels.forEach(group => {
      this._syncParentGroupRefsForGroup(group);
    });

    const unsubscribeStructure = structure.subscribe(onStructureChange);
    const unsubscribeParts = structure.observeParts((part, loaded) => {
      if (loaded) part.elements.observe(onElementsMapChange);
      else part.elements.unobserve(onElementsMapChange);
    });

    const subscription = this.store.slots.blockUpdated.subscribe(payload => {
      switch (payload.type) {
        case 'add':
          if (isGfxGroupCompatibleModel(payload.model)) {
            this._registerGroupLikeModel(payload.id, payload.model);
          }

          // Set _parentGroupId on newly added blocks that belong to an existing group.
          // This handles the case where blocks are loaded after groups during init.
          if ('_parentGroupId' in payload.model) {
            this._syncParentGroupRefForChild(
              payload.id,
              payload.model as { _parentGroupId: string | null }
            );
          }

          break;
        case 'update':
          if (payload.props.key === 'childIds') {
            const model = this.store.getBlock(payload.id)?.model;
            if (isGfxGroupCompatibleModel(model)) {
              this._syncParentGroupRefsForGroup(model);
            }
          }
          break;
        case 'delete':
          if (isGfxGroupCompatibleModel(payload.model)) {
            this._unregisterGroupLikeModel(payload.id);
          }
          {
            const group = this.getGroup(payload.model as GfxModel);
            if (group) {
              // oxlint-disable-next-line unicorn/prefer-dom-node-remove
              group.removeChild(payload.model as GfxModel);
            }
          }

          break;
      }
    });

    this.deleted.subscribe(() => {
      unsubscribeStructure();
      unsubscribeParts();
      subscription.unsubscribe();
    });
  }

  private _propsToY(type: string, props: Record<string, unknown>) {
    const ctor = this._elementCtorMap[type];

    if (!ctor) {
      throw new Error(`Invalid element type: ${type}`);
    }

    Object.entries(props).forEach(([key, val]) => {
      if (val instanceof Object) {
        if (Reflect.has(val, SURFACE_TEXT_UNIQ_IDENTIFIER)) {
          const yText = new Y.Text();
          yText.applyDelta(Reflect.get(val, 'delta'));
          Reflect.set(props, key, yText);
        }

        if (Reflect.has(val, SURFACE_YMAP_UNIQ_IDENTIFIER)) {
          const childJson = Reflect.get(val, 'json') as Record<string, unknown>;
          const childrenYMap = new Y.Map<unknown>();

          Object.keys(childJson).forEach(childId => {
            childrenYMap.set(childId, childJson[childId]);
          });
          Reflect.set(props, key, childrenYMap);
        }
      }
    });

    // @ts-expect-error ignore
    return ctor.propsToY ? ctor.propsToY(props) : props;
  }

  private _watchGroupRelationChange() {
    const isGroup = (
      element: GfxPrimitiveElementModel
    ): element is GfxGroupLikeElementModel =>
      element instanceof GfxGroupLikeElementModel;

    const disposable = this.elementUpdated.subscribe(({ id, oldValues }) => {
      const element = this.getElementById(id)!;

      if (
        isGroup(element) &&
        oldValues['childIds'] &&
        element.childIds.length === 0
      ) {
        this.deleteElement(id);
      }
    });
    this.deleted.subscribe(() => {
      disposable.unsubscribe();
    });
  }

  private _watchChildrenChange() {
    const updateIsEmpty = () => {
      this._isEmpty$.value =
        this._elementModels.size === 0 && this.children.length === 0;
    };

    const disposables = new DisposableGroup();
    disposables.add(this.elementAdded.subscribe(updateIsEmpty));
    disposables.add(this.elementRemoved.subscribe(updateIsEmpty));
    // A child block counts once its model is loaded, not when its entry
    // arrives: its fields may lie in a part loaded later.
    disposables.add(this.children$.subscribe(updateIsEmpty));
    this.deleted.subscribe(() => {
      disposables.dispose();
    });
  }

  protected _extendElement(
    ctorMap: Record<
      string,
      Constructor<
        GfxPrimitiveElementModel,
        ConstructorParameters<typeof GfxPrimitiveElementModel>
      >
    >
  ) {
    Object.assign(this._elementCtorMap, ctorMap);
  }

  protected _init() {
    this._initElementModels();
    this._watchGroupRelationChange();
    this._watchChildrenChange();
  }

  getConstructor(type: string) {
    return this._elementCtorMap[type];
  }

  addElement<T extends object = Record<string, unknown>>(
    props: Partial<T> & { type: string },
    unit?: PageElementUnit
  ) {
    if (this.store.readonly) {
      throw new Error('Cannot add element in readonly mode');
    }

    const middlewareCtx: MiddlewareCtx = {
      type: 'beforeAdd',
      payload: {
        type: props.type,
        props,
      },
    };

    this._middlewares.forEach(mid => mid(middlewareCtx));

    props = middlewareCtx.payload.props as Partial<T> & { type: string };
    if ('xywh' in props && props.xywh !== undefined) {
      props = { ...props, xywh: placeCanvasWorldXYWH(props.xywh) };
    }

    const id = nanoid();

    // @ts-expect-error ignore
    props.id = id;

    // A group's members join it as structure entries; they are not a field.
    const membersProp = Reflect.get(
      this._elementCtorMap[props.type] ?? {},
      'structureMembersProp'
    ) as string | undefined;
    const members =
      membersProp && membersProp in props
        ? structureMemberIds((props as Record<string, unknown>)[membersProp])
        : [];
    if (membersProp) delete (props as Record<string, unknown>)[membersProp];

    // Place the mindmap before its children ask the planner for its unit.
    let initialChildren: unknown =
      props.type === 'mindmap' ? Reflect.get(props, 'children') : undefined;
    let placementRect: readonly [number, number, number, number] | undefined;
    if (initialChildren !== undefined) {
      // Read detached Y.Map input through Yjs itself, before touching page state.
      if (initialChildren instanceof Y.Map) {
        const scratch = initialChildren.doc ? undefined : new Y.Doc();
        try {
          scratch?.getMap('input').set('children', initialChildren);
          initialChildren = initialChildren.toJSON();
        } finally {
          scratch?.destroy();
        }
      }
      const rectangles: Array<readonly [number, number, number, number]> = [];
      const children = initialChildren as Record<string, unknown>;
      if (typeof children.text === 'string') {
        const tree = structuredClone(children);
        initialChildren = tree;
        const target =
          typeof children.xywh === 'string'
            ? deserializeXYWH(children.xywh)
            : typeof Reflect.get(props, 'xywh') === 'string'
              ? deserializeXYWH(Reflect.get(props, 'xywh'))
              : [0, 0, 100, 30];
        const visit = (node: Record<string, unknown>) => {
          if (node.xywh === undefined)
            node.xywh = JSON.stringify([target[0], target[1], 100, 30]);
          rectangles.push(deserializeXYWH(node.xywh));
          (node.children as Record<string, unknown>[] | undefined)?.forEach(
            visit
          );
        };
        visit(tree);
      } else {
        let part: string | undefined;
        for (const id of Object.keys(children)) {
          const entry = this.store.pageStructure.entry(id);
          const node = this.getElementById(id);
          if (!entry || !node)
            throw new PageStructureWriteError('Missing mindmap node ' + id);
          if (part !== undefined && part !== entry.s)
            throw new PageStructureWriteError(
              'Mindmap nodes span page parts',
              'page-part-unit-split'
            );
          part = entry.s;
          unit = { partOf: id };
          rectangles.push(deserializeXYWH(node.xywh));
        }
      }
      if (rectangles.length) {
        const x = Math.min(...rectangles.map(r => r[0]));
        const y = Math.min(...rectangles.map(r => r[1]));
        placementRect = [
          x,
          y,
          Math.max(...rectangles.map(r => r[0] + r[2])) - x,
          Math.max(...rectangles.map(r => r[1] + r[3])) - y,
        ];
      }
      Reflect.deleteProperty(props, 'children');
    }

    const elementModel = this._createElementFromProps(props, {
      onChange: payload => {
        this.elementUpdated.next(payload);
        Object.keys(payload.props).forEach(key => {
          elementModel.model.propsUpdated.next({ key });
        });
      },
    });

    if (this.store.canvasWorld === 'bounded')
      assertCanvasWorldXYWH(elementModel.model.xywh);
    this._elementModels.set(id, elementModel);
    this._pendingElements.add(id);

    this.store.transact(() => {
      this.store.placeElement(
        this.id,
        id,
        elementModel.model.yMap,
        elementModel.model.index,
        placementRect ?? deserializeXYWH(elementModel.model.xywh),
        unit
      );
      if (initialChildren !== undefined)
        Reflect.set(elementModel.model, 'children', initialChildren);
      members.forEach(member => this.store.pageStructure.join(member, id));
    });

    return id;
  }

  addLocalElement(elem: GfxLocalElementModel) {
    this.localElements.add(elem);
    this.localElementAdded.next(elem);
  }

  applyMiddlewares(middlewares: SurfaceMiddleware[]) {
    this._middlewares = middlewares;
  }

  deleteElement(id: string) {
    // In readonly mode, silently return instead of throwing.
    // This happens during history preview when Y.js observers try to sync
    // elements that were removed in the snapshot being loaded.
    if (this.store.readonly) {
      return;
    }

    if (!this.hasElementById(id)) {
      return;
    }

    this.store.transact(() => {
      const element = this.getElementById(id)!;
      const group = this.getGroup(id);

      if (element instanceof GfxGroupLikeElementModel) {
        element.childIds.forEach(childId => {
          if (this.hasElementById(childId)) {
            this.deleteElement(childId);
          } else if (this.store.hasBlock(childId)) {
            this.store.deleteBlock(this.store.getBlock(childId)!.model);
          }
        });
      }

      // oxlint-disable-next-line unicorn/prefer-dom-node-remove
      group?.removeChild(element as GfxModel);

      this.store.pageStructure.writer.removeBlock(id);
    });
  }

  deleteLocalElement(elem: GfxLocalElementModel) {
    if (this.localElements.delete(elem)) {
      this.localElementDeleted.next(elem);
    }
  }

  override dispose(): void {
    super.dispose();

    this.elementAdded.complete();
    this.elementRemoved.complete();
    this.elementUpdated.complete();

    this._elementModels.forEach(({ unmount }) => unmount());
    this._elementModels.clear();
  }

  getElementById(id: string): GfxPrimitiveElementModel | null {
    return this._elementModels.get(id)?.model ?? null;
  }

  getElementsByType(type: string): GfxPrimitiveElementModel[] {
    return this._elementTypeMap.get(type) || [];
  }

  /**
   * Get a group by its ID. O(1) lookup.
   */
  getGroupById(groupId: string): GfxGroupModel | null {
    return this._groupLikeModels.get(groupId) ?? null;
  }

  /**
   * Get the parent group of an element. O(1) lookup via element._parentGroupId.
   */
  getGroup(elem: string | GfxModel): GfxGroupModel | null {
    const element =
      typeof elem === 'string'
        ? ((this.getElementById(elem) ??
            this.store.getBlock(elem)?.model) as GfxModel)
        : elem;

    if (!element) return null;

    // Use O(1) lookup via _parentGroupId when available. During Yjs reloads,
    // a block child can be materialized before the owning surface subscribes
    // to block additions, so repair the missing cache from registered groups.
    if ('_parentGroupId' in element) {
      if (element._parentGroupId) {
        const group = this._groupLikeModels.get(element._parentGroupId);
        if (group) return group;
      }

      const group = this._findParentGroupForChild(element.id);
      element._parentGroupId = group?.id ?? null;
      return group;
    }

    return null;
  }

  /**
   * Get all groups in the group chain. The last group is the top level group.
   * @param id
   * @returns
   */
  getGroups(id: string): GfxGroupModel[] {
    const groups: GfxGroupModel[] = [];
    const visited = new Set<GfxGroupModel>();
    let group = this.getGroup(id);

    while (group) {
      if (visited.has(group)) {
        console.warn('Exists a cycle in group relation');
        break;
      }
      visited.add(group);
      groups.push(group);
      group = this.getGroup(group.id);
    }

    return groups;
  }

  hasElementById(id: string): boolean {
    return this._elementModels.has(id);
  }

  isGroup(element: GfxModel): element is GfxModel & GfxGroupCompatibleInterface;
  isGroup(id: string): boolean;
  isGroup(element: string | GfxModel): boolean {
    if (typeof element === 'string') {
      const el = this.getElementById(element);
      if (el) return isGfxGroupCompatibleModel(el);

      const blockModel = this.store.getBlock(element)?.model;
      if (blockModel) return isGfxGroupCompatibleModel(blockModel);

      return false;
    } else {
      return isGfxGroupCompatibleModel(element);
    }
  }

  updateElement<T extends object = Record<string, unknown>>(
    id: string,
    props: Partial<T>
  ) {
    if (this.store.readonly) {
      throw new Error('Cannot update element in readonly mode');
    }

    const elementModel = this.getElementById(id);

    if (!elementModel) {
      throw new Error(`Element ${id} is not found`);
    }

    this.store.transact(() => {
      const membersProp = Reflect.get(
        this._elementCtorMap[elementModel.type] ?? {},
        'structureMembersProp'
      ) as string | undefined;
      if (membersProp && membersProp in props) {
        const members = new Set(
          structureMemberIds(Reflect.get(props, membersProp))
        );
        const structure = this.store.pageStructure;
        for (const member of structure.members(id)) {
          if (!members.has(member)) structure.leave(member);
        }
        for (const member of members) structure.join(member, id);
        props = { ...props };
        Reflect.deleteProperty(props, membersProp);
      }
      props = this._propsToY(
        elementModel.type,
        props as Record<string, unknown>
      ) as T;
      Object.entries(props).forEach(([key, value]) => {
        // @ts-expect-error ignore
        elementModel[key] = value;
      });
    });
  }
}
